/** Shared renderers for journal lines and who-did-what labels. */
import { Link } from 'react-router';
import { useApi } from '../lib/api';
import { acct } from '../lib/format';
import { useAccounts } from '../lib/lookups';
import type { Line, Members } from '../lib/types';
import { cx, Table, Td, Th } from './ui';

export function EntryLines({ lines, currency, base, compact }: { lines: Line[]; currency: string; base?: boolean; compact?: boolean }) {
  const { byId } = useAccounts();
  const dr = lines.reduce((s, l) => s + (base ? l.baseDebit : l.debit), 0);
  const cr = lines.reduce((s, l) => s + (base ? l.baseCredit : l.credit), 0);
  return (
    <Table dense={compact}>
      <thead>
        <tr>
          <Th>Account</Th>
          <Th>Description</Th>
          {!compact && <Th>Dimensions</Th>}
          <Th right w="130px">
            Debit
          </Th>
          <Th right w="130px">
            Credit
          </Th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => {
          const a = byId.get(l.accountId);
          const d = base ? l.baseDebit : l.debit;
          const c = base ? l.baseCredit : l.credit;
          return (
            <tr key={l.id}>
              <Td>
                <Link to={`/accounts/${l.accountId}`} className="hover:text-brand hover:underline">
                  <span className="num text-muted">{a?.code}</span> {a?.name ?? l.accountId}
                </Link>
              </Td>
              <Td muted className="max-w-[280px] truncate">
                {l.description}
              </Td>
              {!compact && (
                <Td muted className="text-[12px]">
                  {[l.department, ...Object.entries((l.dimensions as Record<string, string> | null) ?? {}).map(([k, v]) => `${k}: ${v}`)].filter(Boolean).join(' · ')}
                </Td>
              )}
              <Td right className={cx(!d && 'text-faint')}>
                {d ? acct(d, currency) : ''}
              </Td>
              <Td right className={cx(!c && 'text-faint')}>
                {c ? acct(c, currency) : ''}
              </Td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr className="font-semibold">
          <Td colSpan={compact ? 2 : 3} className="border-b-0 text-right text-[12px] text-muted">
            Total
          </Td>
          <Td right className="border-b-0">
            {acct(dr, currency)}
          </Td>
          <Td right className="border-b-0">
            {acct(cr, currency)}
          </Td>
        </tr>
      </tfoot>
    </Table>
  );
}

/** "ai:assistant" → "AI assistant", user ids → member names. */
export function useActorLabel() {
  const members = useApi<Members>('/api/members', { staleTime: 5 * 60_000 });
  const names = new Map((members.data ?? []).map((m) => [m.userId, m.name]));
  return (id: string | null | undefined) => {
    if (!id) return '—';
    if (id.startsWith('ai:') || id.startsWith('assistant')) return 'AI assistant';
    if (id.startsWith('mcp:')) return 'MCP agent';
    if (id.startsWith('api_key:') || id.startsWith('key_')) return 'API key';
    if (id.startsWith('system')) return 'System';
    const bare = id.replace(/^user:/, '');
    return names.get(bare) ?? bare;
  };
}
